import { CalendarRange, EllipsisVertical, Pencil } from 'lucide-react'
import { type MouseEvent, useState } from 'react'
import { useNavigate } from 'react-router'

import { getHighestRole, isApproverOrHigherRole } from '@ankor-io/common/domain/iam/account'
import { fromCents } from '@ankor-io/common/domain/pay/currency'
import { Role } from '@ankor-io/common/types/account'
import { type CharterSortBy, CharterStatus } from '@ankor-io/common/types/charter'
import { formatCurrency } from '@ankor-io/common/util/currencies'
import { formatDateShort } from '@ankor-io/common/util/dates'
import { getStatusColour, getStatusText } from '@ankor-io/common/util/enum-to-string/charter'
import { Badge } from '@ankor-io/ui-components/src/Badge'
import { Button } from '@ankor-io/ui-components/src/Button'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@ankor-io/ui-components/src/Dropdown'
import { EmptyState } from '@ankor-io/ui-components/src/EmptyState'
import { Heading, Subheading } from '@ankor-io/ui-components/src/Heading'
import { Loading } from '@ankor-io/ui-components/src/Loading'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@ankor-io/ui-components/src/Table'

import { useSearchChartersQuery } from '@/api/queries'
import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useModuleTheme } from '@/providers/moduleTheme'
import { useUserContext } from '@/providers/user/useUserContext'
import { SortableTableHeader } from '@/views/components/SortableTableHeader'
import {
  type CharterTableSort,
  DEFAULT_CHARTER_TABLE_SORT,
  getDefaultSortOrder,
  getNextCharterSort,
} from '@/views/components/charterTableSort'

const CHARTERS_PER_PAGE = 5
const SKIP = 0

const DashboardCharters = () => {
  const { moduleTheme } = useModuleTheme()
  const navigate = useNavigate()
  const { activeOrganizationId, activeConnectedAccountId, activeOrganizationRole } = useUserContext()

  const [tableSort, setTableSort] = useState<CharterTableSort>(DEFAULT_CHARTER_TABLE_SORT)
  const { sortBy, sortOrder } = tableSort

  // Use React Query to fetch recent charters
  const { data: charters = [], isLoading } = useSearchChartersQuery({
    take: CHARTERS_PER_PAGE,
    skip: SKIP,
    sortBy,
    sortOrder,
    enabled: Boolean(activeOrganizationId) && Boolean(activeConnectedAccountId),
  })

  const showLoader = useMinimumDisplayTime(charters.length === 0 && isLoading, 1000)

  const handleNavigateToCharter = (charterId: string) => {
    navigate(`/apa/charters/${charterId}`)
  }

  const handleEditCharter = (charterId: string, e: MouseEvent) => {
    e.stopPropagation()
    navigate(`/apa/charters/${charterId}/edit`)
  }

  const handleSortChange = (sortKey: CharterSortBy) => {
    setTableSort((current) => getNextCharterSort(current, sortKey))
  }

  return (
    <div className="w-full rounded-lg shadow-sm border divide-y border-gray-200 dark:border-gray-700 divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
      <Heading
        level={4}
        className="p-4"
      >
        Charters Overview
      </Heading>

      <div className="p-4">
        <div className="flex items-center gap-x-4 mb-4">
          <Subheading>Recent charters</Subheading>
          <Button
            color={moduleTheme.button}
            onClick={() => navigate('/apa/charters')}
          >
            View all
          </Button>
        </div>

        <div className="overflow-x-auto">
          <Table className="w-[calc(100vw-4.2rem)] lg:w-[calc(100vw-23.1rem)] xl:w-auto">
            <TableHead>
              <TableRow>
                <SortableTableHeader
                  label="Charter Name"
                  sortKey="name"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                <SortableTableHeader
                  label="Status"
                  sortKey="status"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                <SortableTableHeader
                  label="Start Date"
                  sortKey="startDate"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                <SortableTableHeader
                  label="End Date"
                  sortKey="endDate"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                <SortableTableHeader
                  label="Currency"
                  sortKey="currencyCode"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                <SortableTableHeader
                  className="text-right"
                  label="APA Amount"
                  sortKey="apaCents"
                  activeSortBy={sortBy}
                  activeSortOrder={sortOrder}
                  onSort={handleSortChange}
                  getDefaultSortOrder={getDefaultSortOrder}
                />
                {isApproverOrHigherRole(activeOrganizationRole) && <TableHeader>Actions</TableHeader>}
              </TableRow>
            </TableHead>
            <TableBody>
              {showLoader ? (
                <TableRow>
                  <TableCell
                    colSpan={isApproverOrHigherRole(activeOrganizationRole) ? 7 : 6}
                    className="text-center py-12"
                  >
                    <div className="flex items-center justify-center">
                      <Loading />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                charters.map((charter) => (
                  <TableRow
                    key={charter.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${getHighestRole([activeOrganizationRole]) !== Role.VIEWER ? 'cursor-pointer' : ''}`}
                    onClick={
                      getHighestRole([activeOrganizationRole]) !== Role.VIEWER
                        ? () => handleNavigateToCharter(charter.id)
                        : undefined
                    }
                  >
                    <TableCell>
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-50">{charter.name}</span>
                    </TableCell>
                    <TableCell>
                      <Badge color={getStatusColour(charter.status)}>{getStatusText(charter.status)}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                      {formatDateShort(charter.startDate)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                      {formatDateShort(charter.endDate)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-900 dark:text-gray-100">{charter.currencyCode}</TableCell>
                    <TableCell className="text-sm text-right text-gray-900 dark:text-gray-100">
                      {formatCurrency(fromCents(charter.apaCents), charter.currencyCode)}
                    </TableCell>
                    {isApproverOrHigherRole(activeOrganizationRole) && (
                      <TableCell>
                        {charter.status !== CharterStatus.COMPLETED &&
                          charter.status !== CharterStatus.CANCELLED &&
                          charter.status !== CharterStatus.SIGNED_OFF && (
                            <div className="relative z-10 pointer-events-auto">
                              <Dropdown>
                                <DropdownButton
                                  plain
                                  className="py-0! px-2!"
                                  onClick={(e: MouseEvent) => e.stopPropagation()}
                                >
                                  <EllipsisVertical data-slot="icon" />
                                </DropdownButton>
                                <DropdownMenu anchor="bottom end">
                                  {/* Edit Charter */}
                                  <DropdownItem
                                    className="disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={(e: MouseEvent) => handleEditCharter(charter.id, e)}
                                    data-charter-status={charter.status}
                                    data-action-type="edit-charter"
                                  >
                                    <Pencil data-slot="icon" />
                                    Edit Charter
                                  </DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </div>
                          )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {!showLoader && charters.length === 0 && (
          <EmptyState
            buttonColor={moduleTheme.button}
            heading="No charters found"
            subheading="Get started by creating a new charter."
            icon={<CalendarRange className="mx-auto size-12 text-gray-400 dark:text-gray-500" />}
            buttonText="Create Charter"
            buttonTo="/charters/new"
          />
        )}
      </div>
    </div>
  )
}

export default DashboardCharters
